import { useEffect, useRef, useState } from 'react';
import {
  adminApi,
  type ClaudeDistJob,
  type ClaudeDistDevice,
  type ClaudeAuthProbe,
} from '../api/admin';

// AI-WF #50: Claude 認証はマスタ管理で1個だけ設定し「全端末に配布」する。
// 配布は撃ちっぱなしにせず、各端末×各ステータスがスピナー→実測結果に切り替わる実況中継。

const AUTH_LABEL: Record<ClaudeAuthProbe, string> = {
  ok: '認証OK',
  invalid_token: '不正トークン(401)',
  not_logged_in: '未ログイン',
  expired: '期限切れ',
  rate_limited: 'レート制限',
  api_error: 'API異常',
  no_cli: 'claude無し',
  unknown: '不明',
};

function Spinner() {
  return <span className="kc-spin" aria-label="確認中" />;
}

// セル: 確認中はスピナー、結果は ✓/✗/△ アイコン。
function Cell({
  state,
  ok,
  label,
  tone,
}: {
  state: ClaudeDistDevice['state'];
  ok: boolean | null; // true=✓ false=✗ null=△/—
  label: string;
  tone?: 'good' | 'bad' | 'warn' | 'muted';
}) {
  if (state === 'pending' || state === 'probing') return <td style={tdCell}><Spinner /></td>;
  const color =
    tone === 'good' ? '#166534' : tone === 'bad' ? '#991b1b' : tone === 'warn' ? '#854d0e' : '#64748b';
  const icon = ok === true ? '✓' : ok === false ? '✗' : '△';
  return (
    <td style={tdCell}>
      <span style={{ color, fontWeight: 600 }}>{icon}</span>{' '}
      <span style={{ color, fontSize: 11 }}>{label}</span>
    </td>
  );
}

function overallBadge(d: ClaudeDistDevice) {
  const map = {
    ok: { bg: '#dcfce7', c: '#166534', t: '✓ 正常' },
    pending: { bg: '#f1f5f9', c: '#64748b', t: '△ 保留' },
    warn: { bg: '#fef9c3', c: '#854d0e', t: '⚠ 注意' },
    error: { bg: '#fef2f2', c: '#991b1b', t: '✗ 要対応' },
  } as const;
  const m = map[d.overall];
  return (
    <span style={{ padding: '2px 8px', borderRadius: 999, background: m.bg, color: m.c, fontSize: 12, fontWeight: 600 }}>
      {m.t}
    </span>
  );
}

function DeviceRow({ d }: { d: ClaudeDistDevice }) {
  const s = d.state;
  const done = s === 'done';
  const offline = s === 'offline';
  const timeout = s === 'timeout';
  return (
    <tr>
      <td style={tdName}>
        {d.name}
        <span style={{ color: '#94a3b8', fontSize: 11 }}> #{d.deviceId}</span>
      </td>
      {/* ① 接続 */}
      <Cell
        state={'done'}
        ok={d.online ? true : null}
        label={d.online ? 'オンライン' : 'オフライン'}
        tone={d.online ? 'good' : 'muted'}
      />
      {/* オフライン/タイムアウトは以降を中立で確定。オンラインは probe 中スピナー */}
      {offline ? (
        <>
          <td style={tdCell}><span style={{ color: '#64748b', fontSize: 11 }}>—</span></td>
          <td style={tdCell}><span style={{ color: '#64748b', fontSize: 11 }}>—</span></td>
          <td style={tdCell}><span style={{ color: '#64748b', fontSize: 11 }}>—</span></td>
          <td style={tdCell}><span style={{ color: '#64748b', fontSize: 11 }}>—</span></td>
        </>
      ) : timeout ? (
        <>
          <td style={tdCell}><span style={{ color: '#854d0e', fontSize: 11 }}>⏱ 無応答</span></td>
          <td style={tdCell}><span style={{ color: '#854d0e', fontSize: 11 }}>⏱</span></td>
          <td style={tdCell}><span style={{ color: '#854d0e', fontSize: 11 }}>⏱</span></td>
          <td style={tdCell}><span style={{ color: '#854d0e', fontSize: 11 }}>⏱</span></td>
        </>
      ) : (
        <>
          {/* ② claude 導入 */}
          <Cell
            state={s}
            ok={done ? Boolean(d.cliInstalled) : null}
            label={done ? (d.cliInstalled ? d.cliVersion || '導入済' : '未インストール') : ''}
            tone={d.cliInstalled ? 'good' : 'bad'}
          />
          {/* ③ 鍵=マスタ一致 */}
          <Cell
            state={s}
            ok={done ? Boolean(d.keyMatch) : null}
            label={done ? (d.keyMatch ? 'マスタ一致' : '不一致') : ''}
            tone={d.keyMatch ? 'good' : 'bad'}
          />
          {/* ④ 認証(実測) */}
          <Cell
            state={s}
            ok={done ? d.auth === 'ok' : null}
            label={done && d.auth ? AUTH_LABEL[d.auth] : ''}
            tone={d.auth === 'ok' ? 'good' : d.auth === 'rate_limited' ? 'warn' : 'bad'}
          />
          {/* ⑤ DIR */}
          <Cell
            state={s}
            ok={done ? (d.mounts ? d.mounts.every((m) => m.exists) : null) : null}
            label={
              done
                ? d.mounts && d.mounts.length
                  ? d.mounts.every((m) => m.exists)
                    ? '全存在'
                    : `不在${d.mounts.filter((m) => !m.exists).length}`
                  : '割当なし'
                : ''
            }
            tone={d.mounts && d.mounts.some((m) => !m.exists) ? 'bad' : 'good'}
          />
        </>
      )}
      <td style={tdCell}>{overallBadge(d)}</td>
    </tr>
  );
}

export function ClaudeAuthMasterPage() {
  const [master, setMaster] = useState<{ hasToken: boolean; fingerprint: string | null } | null>(null);
  const [token, setToken] = useState('');
  const [job, setJob] = useState<ClaudeDistJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef<number | null>(null);

  function copyCmd() {
    void navigator.clipboard?.writeText('claude setup-token');
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  useEffect(() => {
    adminApi.getClaudeMaster().then(setMaster).catch(() => undefined);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, []);

  function poll(jobId: string) {
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = window.setInterval(async () => {
      try {
        const j = await adminApi.getClaudeDistribute(jobId);
        if (j) {
          setJob(j);
          if (j.done) {
            if (pollRef.current) window.clearInterval(pollRef.current);
            setBusy(false);
            adminApi.getClaudeMaster().then(setMaster).catch(() => undefined);
          }
        }
      } catch {
        /* 次周期で再試行 */
      }
    }, 600);
  }

  async function distribute(withToken: boolean) {
    setError(null);
    setBusy(true);
    setJob(null);
    try {
      const j = await adminApi.startClaudeDistribute(withToken ? token.trim() : undefined);
      setJob(j);
      poll(j.jobId);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err?.response?.data?.message ?? err?.message ?? '配布に失敗しました');
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 1000 }}>
      <style>{`@keyframes kcspin{to{transform:rotate(360deg)}}
        .kc-spin{display:inline-block;width:14px;height:14px;border:2px solid #cbd5e1;border-top-color:#2563eb;border-radius:50%;animation:kcspin .7s linear infinite;vertical-align:middle}`}</style>
      <h3 style={{ margin: '0 0 4px' }}>Claude 認証（マスタ）</h3>
      <p className="k-muted" style={{ marginTop: 0, fontSize: 13 }}>
        鍵は<strong>1個だけ</strong>ここで設定し「全端末に配布」する。デバイス側で個別に鍵を入力する必要はない。
      </p>

      <div style={card}>
        <div style={{ marginBottom: 8 }}>
          現在のマスタ:{' '}
          {master?.hasToken ? (
            <span style={{ color: '#166534', fontWeight: 600 }}>設定済 (指紋 {master.fingerprint})</span>
          ) : (
            <span style={{ color: '#991b1b', fontWeight: 600 }}>未設定</span>
          )}
        </div>
        <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
          <div style={{ marginBottom: 6 }}>
            ① 自分の PC のターミナルで<strong>これを1回だけ実行</strong>:
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: '#0f172a',
              color: '#e2e8f0',
              borderRadius: 8,
              padding: '8px 10px',
              fontFamily: 'monospace',
              fontSize: 13,
            }}
          >
            <span style={{ flex: 1 }}>claude setup-token</span>
            <button
              type="button"
              className="btn"
              style={{ padding: '2px 10px', fontSize: 12 }}
              onClick={copyCmd}
            >
              {copied ? '✓ コピーした' : 'コピー'}
            </button>
          </div>
          <div style={{ marginTop: 6 }}>
            ② ブラウザが開く→承認→<strong>「認証コード」</strong>が出る → その<strong>コードをターミナルに戻して貼り付け→Enter</strong>。
          </div>
          <div style={{ marginTop: 4 }}>
            ③ するとターミナルに <code>sk-ant-oat01-…</code>（約1年有効）が出る。<strong>それ</strong>を下に貼り付け → ④「保存して全端末に配布」。
          </div>
          <div
            style={{
              marginTop: 6,
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 6,
              padding: '6px 8px',
              color: '#854d0e',
              fontSize: 12,
            }}
          >
            ⚠ ブラウザに出る「認証コード」（例 <code>pn07…</code>）は<strong>トークンではありません</strong>。
            それはターミナルに戻して貼るもの。下の欄に貼るのは <code>sk-ant-oat01-…</code> だけです。
          </div>
        </div>
        <textarea
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="sk-ant-oat01-…"
          rows={2}
          style={{ width: '100%', fontFamily: 'monospace', fontSize: 12, padding: 8, boxSizing: 'border-box' }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <button
            className="btn-primary"
            type="button"
            disabled={busy || token.trim().length === 0}
            onClick={() => distribute(true)}
          >
            保存して全端末に配布
          </button>
          <button
            className="btn"
            type="button"
            disabled={busy || !master?.hasToken}
            onClick={() => distribute(false)}
            title="今 DB にあるマスタをそのまま全端末へ再配布・再確認する"
          >
            現マスタを全端末に再配布・確認
          </button>
        </div>
        {error && <div style={{ color: '#991b1b', marginTop: 8, fontSize: 13 }}>{error}</div>}
      </div>

      {job && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <strong>配布の実況</strong>
            {!job.done && <Spinner />}
            <span className="k-muted" style={{ fontSize: 12 }}>
              {job.done ? '完了' : '各端末に適用・実認証を確認中…'}
            </span>
          </div>
          {job.done && job.masterSuspect && (
            <div
              style={{
                background: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fecaca',
                borderRadius: 8,
                padding: '8px 10px',
                marginBottom: 8,
                fontSize: 13,
              }}
            >
              ❗ オンライン機が揃って認証失敗。<strong>マスタトークン自体が無効/期限切れの疑い</strong>。
              <code>claude setup-token</code> を再発行して配布し直してください。
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={th}>デバイス</th>
                  <th style={th}>接続</th>
                  <th style={th}>claude</th>
                  <th style={th}>鍵(マスタ一致)</th>
                  <th style={th}>認証(実測)</th>
                  <th style={th}>DIR</th>
                  <th style={th}>総合</th>
                </tr>
              </thead>
              <tbody>
                {job.devices.map((d) => (
                  <DeviceRow key={d.deviceId} d={d} />
                ))}
              </tbody>
            </table>
          </div>
          <p className="k-muted" style={{ fontSize: 11, marginBottom: 0 }}>
            オフライン機は「△保留」。次に接続した瞬間に自動でマスタが適用されます（再配布不要）。
          </p>
        </div>
      )}
    </div>
  );
}

const card: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  padding: 14,
  margin: '12px 0',
  background: '#fff',
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '2px solid #e2e8f0',
  fontSize: 12,
  color: '#475569',
  whiteSpace: 'nowrap',
};
const tdName: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid #f1f5f9',
  whiteSpace: 'nowrap',
  fontWeight: 600,
};
const tdCell: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid #f1f5f9',
  whiteSpace: 'nowrap',
};
